import React, { useState, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { login as authLogin, selectLoginContext } from '../services/authService';
import UserContextSelector, { rememberLastContext } from '../components/UserContextSelector';
import {
  EyeIcon,
  EyeSlashIcon,
  EnvelopeIcon,
  LockClosedIcon,
  HomeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Multi-identity selection state — populated when /login returns
  // requires_context_selection=true. While non-null, render the selector
  // instead of the login form.
  const [pendingSelection, setPendingSelection] = useState(null);
  const [selectionLoading, setSelectionLoading] = useState(false);
  const [selectionError, setSelectionError] = useState(null);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Optional one-shot success flash set by navigate(..., { state: { flash } })
  // — used by the accept-invitation page to confirm activation.
  const flash = location.state?.flash;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authLogin(formData.email, formData.password);

      // Multi-identity flow — show selector instead of completing login.
      if (response.requires_context_selection) {
        setPendingSelection({
          selectionToken: response.selection_token,
          contexts: response.available_contexts || [],
          user: response.user || {},
        });
        return;
      }

      if (response.user || response.tokens || response.token) {
        login(response);
        navigate('/properties');
      } else {
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Called when the user picks a card in the selector.
  const handleContextSelect = async (ctx) => {
    if (!pendingSelection) return;
    setSelectionLoading(true);
    setSelectionError(null);

    const isPlatformCtx = ctx.type === 'system_admin' || ctx.type === 'account_manager';

    try {
      const finalResponse = await selectLoginContext({
        selectionToken: pendingSelection.selectionToken,
        contextType: ctx.type,
        // Platform contexts have no agency entity — omit agent/renter/owner IDs.
        agentId: isPlatformCtx ? undefined : ctx.agent_id,
        renterId: isPlatformCtx ? undefined : ctx.renter_id,
        ownerId: isPlatformCtx ? undefined : ctx.owner_id,
      });

      // Remember the choice so we can pre-highlight next time.
      if (pendingSelection.user?.id) {
        if (isPlatformCtx) {
          rememberLastContext(pendingSelection.user.id, { type: ctx.type, id: '__platform__' });
        } else {
          const chosenId = ctx.agent_id ?? ctx.renter_id ?? ctx.owner_id;
          if (chosenId != null) {
            rememberLastContext(pendingSelection.user.id, { type: ctx.type, id: chosenId });
          }
        }
      }

      login(finalResponse);
      navigate('/properties');
    } catch (err) {
      if (err.status === 401) {
        // Selection token expired — fall back to the login form.
        setPendingSelection(null);
        setError('Your session expired. Please sign in again.');
      } else {
        setSelectionError(err.message || 'Failed to complete sign in.');
      }
    } finally {
      setSelectionLoading(false);
    }
  };

  const handleSelectionCancel = () => {
    setPendingSelection(null);
    setSelectionError(null);
    setFormData((f) => ({ ...f, password: '' }));
  };

  // Render the identity selector instead of the form when needed.
  if (pendingSelection) {
    return (
      <UserContextSelector
        contexts={pendingSelection.contexts}
        platformRole={pendingSelection.user?.platform_role || null}
        userEmail={pendingSelection.user?.email || formData.email}
        userId={pendingSelection.user?.id}
        onSelect={handleContextSelect}
        onCancel={handleSelectionCancel}
        loading={selectionLoading}
        error={selectionError}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-blue-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-blue-500/20 backdrop-blur-3xl"></div>

      <div className="max-w-md w-full space-y-8 relative">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-3 rounded-2xl shadow-lg">
              <HomeIcon className="h-12 w-12 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">Welcome back</h2>
          <p className="mt-2 text-sm text-gray-600">Sign in to your rental management account</p>
        </div>

        <div className="bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {flash && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm">{flash}</span>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div className="text-sm text-red-800">{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">Remember me</label>
              </div>
              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-emerald-600 hover:text-emerald-500 transition-colors">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <a href="/register" className="font-medium text-emerald-600 hover:text-emerald-500 transition-colors">
                  Sign up here
                </a>
              </p>
            </div>
          </form>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">© 2024 Rental Management System. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;